import { FC, useState, FormEvent } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { login } from '../../store/slices/authSlice';
import { apiListGetUserByToken } from '../ui_components/ApiList';
import type { LoginCredentials } from '../../types';

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
`;

const ModalDialog = styled.div`
  position: relative;
  width: 100%;
  max-width: 500px;
  margin: 28px auto;
  pointer-events: none;
`;

const ModalContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  pointer-events: auto;
  background-color: #ffffff;
  background-clip: padding-box;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 5px;
  outline: 0;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid #dee2e6;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
`;

const ModalTitle = styled.h5`
  margin: 0;
  line-height: 1.5;
  font-size: 20px;
  font-weight: 500;
  color: #000000;
`;

const CloseButton = styled.button`
  box-sizing: content-box;
  width: 1em;
  height: 1em;
  padding: 4px;
  color: #000000;
  border: 0;
  border-radius: 4px;
  opacity: 0.5;
  background: transparent;
  cursor: pointer;
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
  text-shadow: 0 1px 0 #fff;

  &:hover {
    opacity: 0.75;
  }

  &:before {
    content: '×';
  }
`;

const ModalBody = styled.div`
  position: relative;
  flex: 1 1 auto;
  padding: 16px;
  color: #000000;
`;

const ModalFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-top: 1px solid #dee2e6;
  border-bottom-right-radius: 4px;
  border-bottom-left-radius: 4px;
  gap: 8px;
`;

const FooterLeft = styled.div`
  display: flex;
  align-items: center;
`;

const FooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

interface FormGroupProps {
  mb?: string;
}

const FormGroup = styled.div<FormGroupProps>`
  margin-bottom: ${(props) => props.mb || '16px'};
`;

const FormLabel = styled.label`
  display: inline-block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #000000;
`;

const FormInput = styled.input`
  display: block;
  width: 100%;
  padding: 6px 12px;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #000000;
  background-color: #ffffff;
  background-image: none;
  border: 1px solid #d3d3d3;
  border-radius: 4px;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    color: #000000;
    background-color: #ffffff;
    border-color: #86b7fe;
    outline: 0;
    box-shadow: 0 0 0 4px rgba(13, 110, 253, 0.25);
  }

  &:disabled {
    background-color: #e9ecef;
    opacity: 1;
  }
`;

const Button = styled.button`
  display: inline-block;
  font-weight: 400;
  line-height: 1.5;
  color: #000000;
  text-align: center;
  text-decoration: none;
  vertical-align: middle;
  cursor: pointer;
  user-select: none;
  background-color: transparent;
  border: 1px solid transparent;
  padding: 6px 12px;
  font-size: 16px;
  border-radius: 4px;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const ButtonPrimary = styled(Button)`
  color: #ffffff;
  background-color: #0d6efd;
  border-color: #0d6efd;

  &:hover:not(:disabled) {
    color: #ffffff;
    background-color: #0b5ed7;
    border-color: #0a58ca;
  }

  &:focus {
    color: #ffffff;
    background-color: #0b5ed7;
    border-color: #0a58ca;
    box-shadow: 0 0 0 4px rgba(49, 132, 253, 0.5);
  }
`;

const ButtonSecondary = styled(Button)`
  color: #ffffff;
  background-color: #6c757d;
  border-color: #6c757d;

  &:hover:not(:disabled) {
    color: #ffffff;
    background-color: #5c636a;
    border-color: #565e64;
  }

  &:focus {
    color: #ffffff;
    background-color: #5c636a;
    border-color: #565e64;
    box-shadow: 0 0 0 4px rgba(130, 138, 145, 0.5);
  }
`;

const ButtonDanger = styled(Button)`
  color: #ffffff;
  background-color: #dc3545;
  border-color: #dc3545;

  &:hover:not(:disabled) {
    color: #ffffff;
    background-color: #bb2d3b;
    border-color: #b02a37;
  }

  &:focus {
    color: #ffffff;
    background-color: #bb2d3b;
    border-color: #b02a37;
    box-shadow: 0 0 0 4px rgba(225, 83, 97, 0.5);
  }
`;

const ButtonOutlineSecondary = styled(Button)`
  color: #6c757d;
  border-color: #6c757d;
  margin-top: 8px;

  &:hover:not(:disabled) {
    color: #ffffff;
    background-color: #6c757d;
    border-color: #6c757d;
  }

  &:focus {
    box-shadow: 0 0 0 4px rgba(108, 117, 125, 0.5);
  }
`;

const ErrorText = styled.p`
  margin-top: 4px;
  margin-bottom: 0;
  color: #dc3545;
  font-size: 14px;
`;

const StatusMessage = styled.div`
  text-align: right;
  padding: 0 16px;
  margin-top: 8px;
  color: #000000;
`;

const ForgotPasswordLink = styled.span`
  display: inline-block;
  margin-top: 8px;
  color: #0d6efd;
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #0a58ca;
  }
`;

interface LoginModaleProps {
  closeModalClick: () => void;
  closeLoginOpenRegistrazione: () => void;
  openForgotPassword: () => void;
  logUser: (data: LoginCredentials) => Promise<unknown>;
}

const LoginModale: FC<LoginModaleProps> = ({
  closeModalClick,
  closeLoginOpenRegistrazione,
  openForgotPassword,
  logUser,
}) => {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [errorsForm, setErrorsForm] = useState<Record<string, string>>({});
  const [isLoadingForm, setIsLoadingForm] = useState<boolean>(false);

  const dispatch = useDispatch();

  const togglePasswordVisibility = (): void => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setIsLoadingForm(true);
    setErrorsForm({});
    const formData = new FormData(event.currentTarget);
    const data: LoginCredentials = {
      email: formData.get('email') as string,
      password: formData.get('password') as string,
    };
    try {
      const result = (await logUser(data)) as any;
      if (result?.access_token) {
        const user = result.user ?? (await apiListGetUserByToken());
        if (user) dispatch(login(user));
        closeModalClick();
      } else if (result?.errors) {
        setErrorsForm(result.errors);
      } else {
        setErrorsForm({ general: result?.message ?? 'Credenziali non valide' });
      }
    } catch (error) {
      console.error(error);
      setErrorsForm({ general: 'Si è verificato un errore. Riprova.' });
    } finally {
      setIsLoadingForm(false);
    }
  };

  return (
    <Container>
      <ModalDialog>
        <ModalContent>
          <form onSubmit={handleSubmit}>
            <ModalHeader>
              <ModalTitle>Login</ModalTitle>
              <CloseButton type="button" aria-label="Close" onClick={closeModalClick} />
            </ModalHeader>
            <ModalBody>
              {errorsForm.general && <ErrorText>{errorsForm.general}</ErrorText>}
              <FormGroup>
                <FormLabel htmlFor="email">Username o email:</FormLabel>
                <FormInput type="text" id="email" name="email" />
                {errorsForm.email && <ErrorText>{errorsForm.email}</ErrorText>}
              </FormGroup>

              <FormGroup mb="8px">
                <FormLabel htmlFor="password">Password:</FormLabel>
                <FormInput
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                />
                {errorsForm.password && <ErrorText>{errorsForm.password}</ErrorText>}
                <ButtonOutlineSecondary type="button" onClick={togglePasswordVisibility}>
                  {showPassword ? 'Nascondi' : 'Mostra Password'}
                </ButtonOutlineSecondary>
              </FormGroup>

              <ForgotPasswordLink onClick={openForgotPassword}>
                Password dimenticata?
              </ForgotPasswordLink>
            </ModalBody>
            <ModalFooter>
              <FooterLeft>
                <ButtonDanger type="button" onClick={closeLoginOpenRegistrazione}>
                  Registrati
                </ButtonDanger>
              </FooterLeft>

              <FooterRight>
                <ButtonSecondary type="button" onClick={closeModalClick}>
                  Chiudi
                </ButtonSecondary>
                <ButtonPrimary type="submit">Login</ButtonPrimary>
              </FooterRight>
            </ModalFooter>
            {isLoadingForm && <StatusMessage>Caricamento..</StatusMessage>}
          </form>
        </ModalContent>
      </ModalDialog>
    </Container>
  );
};

export default LoginModale;
